import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    TablePagination,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid
} from "@mui/material";
import {
    KeyboardArrowDown,
    KeyboardArrowUp,
    Edit,
    Delete
} from "@mui/icons-material";
import {
    getPackageRateSchedules,
    cancelPackageRateSchedule
} from "../../Services/Packages.service";
import { showAlert } from "../../Services/Shared.service";
import moment from "moment";

const PackageRateScheduleTable = ({ packageRateData, refreshSchedules, onEditSchedule }) => {

    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedRowId, setExpandedRowId] = useState(null);


    // Pagination state
    const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 3 });
    const [totalCount, setTotalCount] = useState(0);

    // Dialog State
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [scheduleToCancel, setScheduleToCancel] = useState(null);

    // Common styles for table header cells
    const tableHeaderCellStyle = {
        backgroundColor: "#E8F5E9",
        color: "#333",
        borderBottom: "none",
        fontWeight: "600",
        verticalAlign: "middle"
    };

    // Fetch schedules on mount and when pagination changes
    useEffect(() => {
        if (packageRateData?.id) {
            fetchSchedules();
        }
    }, [packageRateData?.id, pagination.page, pagination.rowsPerPage, refreshSchedules]);

    const fetchSchedules = async () => {
        setLoading(true);
        try {
            const options = {
                page: pagination.page,
                size: pagination.rowsPerPage,
                status: "PENDING",  // Always fetch only PENDING schedules
            };

            const response = await getPackageRateSchedules(packageRateData.id, options);
            if (response?.data?.list) {
                setSchedules(response.data.list);
                setTotalCount(response.data.count || 0);
            } else {
                 setSchedules([]);
                 setTotalCount(0);
            }
        } catch (error) {
            console.error("Error fetching schedules:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    };

    const handleChangeRowsPerPage = (event) => {
        setPagination({
            page: 0,
            rowsPerPage: parseInt(event.target.value, 10),
        });
    };


    const handleExpandRow = (scheduleId) => {
        if (expandedRowId === scheduleId) {
            setExpandedRowId(null);
        } else {
            setExpandedRowId(scheduleId);
        }
    };

    const handleEditClick = (schedule, e) => {
        e.stopPropagation();
        if (onEditSchedule) {
            onEditSchedule(schedule);
        }
    };

    const handleDelete = async (scheduleId, e) => {
        e.stopPropagation();
        setScheduleToCancel(scheduleId);
        setOpenCancelDialog(true);
    };

    const confirmCancelSchedule = async () => {
        if (!scheduleToCancel) return;

        try {
            await cancelPackageRateSchedule(scheduleToCancel);
            showAlert("Schedule cancelled successfully", "success");
            fetchSchedules();
        } catch (error) {
            console.error("Error cancelling schedule:", error);
            showAlert(error?.response?.data?.error || "Failed to cancel schedule", "error");
        } finally {
            setOpenCancelDialog(false);
            setScheduleToCancel(null);
        }
    };

    const formatTime = (seconds) => {
        if (!seconds && seconds !== 0) return "-";
        return moment().startOf('day').add(seconds, 'seconds').format("hh:mm A");
    };

    const getStatusBadge = (status) => {
        const styles = {
            PENDING: { bg: "#FFF4E5", color: "#663C00", border: "1px solid #FF9800" },
            APPLIED: { bg: "#E8F5E9", color: "#2E7D32", border: "1px solid #4CAF50" },
            CANCELLED: { bg: "#F5F5F5", color: "#616161", border: "1px solid #9E9E9E" },
            FAILED: { bg: "#FFEBEE", color: "#C62828", border: "1px solid #EF5350" },
            EXPIRED: { bg: "#FFEBEE", color: "#C62828", border: "1px solid #EF5350" }
        };
        const style = styles[status] || styles.CANCELLED;

        return (
            <Chip
                label={status?.charAt(0) + status?.slice(1).toLowerCase()}
                size="small"
                sx={{
                    backgroundColor: style.bg,
                    color: style.color,
                    border: style.border,
                    fontWeight: "500",
                    fontSize: "12px"
                }}
            />
        );
    };


    // Desktop Table View - Read-Only Details
    const renderViewDetails = (schedule) => (
        <Box sx={{ p: 2, backgroundColor: "#fff", borderTop: "1px solid #eee" }}>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">Time From</Typography>
                    <Typography>{schedule.isTimeBased ? formatTime(schedule.timeFrom) : "-"}</Typography>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">Time To</Typography>
                    <Typography>{schedule.isTimeBased ? formatTime(schedule.timeTo) : "-"}</Typography>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">Minimum Distance</Typography>
                    <Typography>{schedule.minPkgDistance} km</Typography>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">Maximum Distance</Typography>
                    <Typography>{schedule.maxPkgDistance} km</Typography>
                </Grid>
            </Grid>
        </Box>
    );


    if (!packageRateData?.id) return null;

    return (
        <>
            <Box sx={{ mt: 4, mb: 4 }}>
                {/* Header */}
                <Box sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                    gap: 2
                }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#333", fontSize: "20px" }}
                    >
                        Upcoming Package Rates
                    </Typography>
                </Box>

                {/* Content */}
                {loading ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="textSecondary">Loading...</Typography>
                    </Box>
                ) : (
                    <>
                            <TableContainer component={Paper} sx={{ borderRadius: "8px", border: "1px solid #dee2e6" }}>
                                <Table sx={{ fontSize: "14px" }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    ...tableHeaderCellStyle,
                                                    width: "40px"
                                                }}
                                            />
                                            <TableCell sx={tableHeaderCellStyle}>
                                                Effective Date
                                            </TableCell>
                                            <TableCell sx={tableHeaderCellStyle}>
                                                Per Km Charge
                                            </TableCell>
                                            <TableCell sx={tableHeaderCellStyle}>
                                                Package Fee
                                            </TableCell>
                                            <TableCell sx={tableHeaderCellStyle}>
                                                Status
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...tableHeaderCellStyle,
                                                    width: "100px"
                                                }}
                                            >
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {schedules.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                                    No scheduled changes found
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            schedules.map((schedule) => (
                                                <React.Fragment key={schedule.id}>
                                                    <TableRow
                                                        onClick={() => handleExpandRow(schedule.id)}
                                                        sx={{
                                                            cursor: "pointer",
                                                            backgroundColor: expandedRowId === schedule.id ? "#FAFAFA" : "white",
                                                            '&:hover': {
                                                                backgroundColor: "#f5f5f5"
                                                            }
                                                        }}
                                                    >
                                                        <TableCell align="center">
                                                            <IconButton size="small">
                                                                {expandedRowId === schedule.id ?
                                                                    <KeyboardArrowUp fontSize="small" /> :
                                                                    <KeyboardArrowDown fontSize="small" />
                                                                }
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell sx={{ color: "#2E7D32", fontWeight: "500" }}>
                                                            {moment(schedule.effectiveDate).format("DD-MM-YYYY")}
                                                        </TableCell>
                                                        <TableCell>{schedule.addnlPerKmCharge}</TableCell>
                                                        <TableCell>{schedule.pkgCharge}</TableCell>
                                                        <TableCell>{getStatusBadge(schedule.status)}</TableCell>
                                                        <TableCell>
                                                            {schedule.status === "PENDING" && (
                                                                <Box sx={{ display: "flex", gap: "5px" }}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => handleEditClick(schedule, e)}
                                                                        color="default"
                                                                    >
                                                                        <Edit fontSize="small" sx={{ fontSize: "18px" }} />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => handleDelete(schedule.id, e)}
                                                                        color="default"
                                                                    >
                                                                        <Delete fontSize="small" sx={{ fontSize: "18px" }} />
                                                                    </IconButton>
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    {expandedRowId === schedule.id && (
                                                        <TableRow>
                                                            <TableCell colSpan={6} sx={{ padding: 0 }}>
                                                                {renderViewDetails(schedule)}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        {/* Pagination */}
                        {totalCount > 0 && (
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                alignItems="center"
                                sx={{ mt: 2 }}
                            >
                                <TablePagination
                                    component="div"
                                    count={totalCount}
                                    page={pagination.page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={pagination.rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[3, 5]}
                                    labelRowsPerPage="Rows per page:"
                                    sx={{
                                        '& .MuiTablePagination-selectLabel': {
                                            m: 'auto',
                                            fontSize: "14px"
                                        },
                                        '& .MuiTablePagination-displayedRows': {
                                            m: 'auto',
                                            fontSize: "14px"
                                        },
                                        '& .MuiTablePagination-select': {
                                            fontSize: "14px"
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            <Dialog
                open={openCancelDialog}
                onClose={() => setOpenCancelDialog(false)}
            >
                <DialogTitle>Confirm Cancellation</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to cancel this scheduled change?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCancelDialog(false)} sx={{ color: '#666' }}>
                        No
                    </Button>
                    <Button
                        onClick={confirmCancelSchedule}
                        variant="contained"
                        autoFocus
                        sx={{ backgroundColor: '#dc3545', '&:hover': { backgroundColor: '#c82333' } }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PackageRateScheduleTable;